import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toTitleCase } from "@/lib/text-utils";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, SearchCode, Search, Bell, BellRing, Table2, Loader2, ChevronDown, ChevronRight, Columns, Bookmark, MoreVertical, Database, ArrowLeft, Copy, FileText, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { format } from "date-fns";
import QueryEditor from "./QueryEditor";
import SubscribeDialog from "./SubscribeDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { useMcpSchema } from "@/hooks/useMcpSchema";

interface SavedQuery {
  id: string;
  name: string;
  description: string | null;
  database_connection_id: string | null;
  sql_query: string;
  builder_config: any;
  generation_mode: string;
  created_at: string;
  updated_at: string;
  bookmarked: boolean;
  is_report: boolean;
  pipeline_source_connector?: { name: string } | null;
}

const SavedQueries = () => {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [subscribeQuery, setSubscribeQuery] = useState<SavedQuery | null>(null);
  const [nameFilter, setNameFilter] = useState("");

  // Select mode: user arrived from workflow editor or widget dialog to pick a query
  const selectMode = searchParams.get("selectMode") === "1";
  const fromSource = searchParams.get("from");
  const fromWidgetDialog = fromSource === "widget";
  const fromEditWidgetDialog = fromSource === "edit_widget";

  const handleSelectQuery = (q: SavedQuery) => {
    // Add widget flow: store selected query and navigate back to dashboards
    if (fromWidgetDialog) {
      sessionStorage.setItem(
        "widget_selected_query",
        JSON.stringify({ id: q.id, name: q.name, sql_query: q.sql_query })
      );
      const draftRaw = sessionStorage.getItem("widget_draft");
      let dashboardId = "";
      if (draftRaw) {
        try { dashboardId = JSON.parse(draftRaw).dashboardId || ""; } catch { /* ignore */ }
      }
      navigate(dashboardId ? `/dashboards?dashboard=${dashboardId}` : "/dashboards");
      return;
    }

    // Edit widget flow: store selected query and navigate back to dashboards
    if (fromEditWidgetDialog) {
      sessionStorage.setItem(
        "edit_widget_selected_query",
        JSON.stringify({ id: q.id, name: q.name, sql_query: q.sql_query })
      );
      const draftRaw = sessionStorage.getItem("edit_widget_draft");
      let dashboardId = "";
      if (draftRaw) {
        try { dashboardId = JSON.parse(draftRaw).dashboardId || ""; } catch { /* ignore */ }
        sessionStorage.removeItem("edit_widget_draft");
      }
      navigate(dashboardId ? `/dashboards?dashboard=${dashboardId}` : "/dashboards");
      return;
    }

    // Workflow flow: update workflow draft and navigate back
    const raw = sessionStorage.getItem("wf_draft");
    const nodeId = sessionStorage.getItem("wf_draft_node_id");
    if (raw && nodeId) {
      try {
        const draft = JSON.parse(raw);
        draft.nodes = draft.nodes.map((n: any) =>
          n.id === nodeId
            ? { ...n, config: { ...n.config, query: q.sql_query, label: n.config.label || q.name } }
            : n
        );
        sessionStorage.setItem("wf_draft", JSON.stringify(draft));
      } catch {
        /* ignore parse errors */
      }
      sessionStorage.removeItem("wf_draft_node_id");
    }
    navigate("/aiagents");
  };
  const [schemaOpen, setSchemaOpen] = useState(false);
  const [schemaSearch, setSchemaSearch] = useState("");
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set());

  const { schemaData, tables: schemaTables, columns: schemaColumns, isLoading: schemaLoading, refetch: refetchSchema } = useMcpSchema();

  const { data: subscriptions = [] } = useQuery({
    queryKey: ["query-subscriptions-all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("query_subscriptions" as any)
        .select("saved_query_id, enabled");
      if (error) throw error;
      return (data || []) as any[];
    },
  });

  const subscriptionMap = new Map(
    subscriptions.map((s: any) => [s.saved_query_id, s.enabled])
  );

  const { data: queries = [], isLoading } = useQuery({
    queryKey: ["saved-queries"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("saved_queries" as any)
        .select("*, pipeline_source_connector(name)")
        .is("link_type", null)
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return ((data || []) as any[]).map((q: any) => ({ ...q, bookmarked: !!q.bookmarked, is_report: !!q.is_report })) as unknown as SavedQuery[];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("saved_queries" as any)
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["saved-queries"] });
      toast.success("Query deleted");
    },
    onError: () => toast.error("Failed to delete query"),
  });

  const cloneMutation = useMutation({
    mutationFn: async (query: SavedQuery) => {
      const payload: any = {
        name: `${query.name} (Copy)`,
        description: query.description || null,
        database_connection_id: query.database_connection_id,
        sql_query: query.sql_query,
        builder_config: query.builder_config,
        generation_mode: query.generation_mode,
        is_report: query.is_report,
        client_id: (query as any).client_id || profile?.client_id,
        profile_id: profile?.id,
        link_type: (query as any).link_type || null,
        link_id: (query as any).link_id || null,
      };
      const { error } = await supabase.from("saved_queries" as any).insert(payload);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["saved-queries"] });
      toast.success("Query cloned");
    },
    onError: () => toast.error("Failed to clone query"),
  });

  const toggleBookmark = async (id: string) => {
    const query = queries.find((q) => q.id === id);
    if (!query) return;
    const newVal = !query.bookmarked;
    queryClient.setQueryData(["saved-queries"], (old: SavedQuery[] | undefined) =>
      (old || []).map((q) => q.id === id ? { ...q, bookmarked: newVal } : q)
    );
    await supabase.from("saved_queries" as any).update({ bookmarked: newVal } as any).eq("id", id);
  };

  const toggleReport = async (id: string) => {
    const query = queries.find((q) => q.id === id);
    if (!query) return;
    const newVal = !query.is_report;
    queryClient.setQueryData(["saved-queries"], (old: SavedQuery[] | undefined) =>
      (old || []).map((q) => q.id === id ? { ...q, is_report: newVal } : q)
    );
    await supabase.from("saved_queries" as any).update({ is_report: newVal } as any).eq("id", id);
    queryClient.invalidateQueries({ queryKey: ["report-queries"] });
  };

  const editorParam = searchParams.get("editor");
  const editorOpen = !!editorParam;
  const fromWidget = searchParams.get("fromWidget") === "1";

  // When arriving from a dashboard widget "View as Table" action, build a synthetic query
  const [widgetQuery] = useState<{ sql_query: string; name: string; generation_mode: string } | null>(() => {
    if (!fromWidget) return null;
    const sql = sessionStorage.getItem("widget-view-table-sql");
    const title = sessionStorage.getItem("widget-view-table-title");
    sessionStorage.removeItem("widget-view-table-sql");
    sessionStorage.removeItem("widget-view-table-title");
    if (!sql) return null;
    return { sql_query: sql, name: title || "Widget Query", generation_mode: "raw" };
  });

  const editingQuery = editorParam && editorParam !== "new"
    ? (queries.find((q) => q.id === editorParam) ?? null)
    : widgetQuery;

  const handleNew = () => {
    setSearchParams((prev) => { prev.set("editor", "new"); return prev; });
  };

  const handleEdit = (q: SavedQuery) => {
    setSearchParams((prev) => { prev.set("editor", q.id); return prev; });
  };

  const handleCloseEditor = () => {
    setSearchParams((prev) => { prev.delete("editor"); return prev; });
  };

  const modeBadgeVariant = (mode: string) => {
    switch (mode) {
      case "builder": return "default";
      case "ai": return "secondary";
      case "raw": return "outline";
      default: return "outline";
    }
  };

  // Full-page editor mode
  if (editorOpen) {
    return (
      <QueryEditor
        onClose={handleCloseEditor}
        editingQuery={editingQuery}
        schemaTables={schemaTables}
        schemaColumns={schemaColumns}
        autoRun={fromWidget && !!widgetQuery}
      />
    );
  }

  const toggleTable = (tableName: string) => {
    setExpandedTables((prev) => {
      const next = new Set(prev);
      if (next.has(tableName)) next.delete(tableName);
      else next.add(tableName);
      return next;
    });
  };

  const filteredSchema = schemaData.filter(
    (t) =>
      t.table_name.toLowerCase().includes(schemaSearch.toLowerCase()) ||
      t.columns.some((c) => c.column_name.toLowerCase().includes(schemaSearch.toLowerCase()))
  );

  return (
    <div className="flex flex-1 flex-col gap-4 p-6 min-h-0 overflow-hidden">
      {selectMode && (
        <div className="rounded-md border border-indigo-200 bg-indigo-50 px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-indigo-700 font-medium">
            <Database className="h-4 w-4 shrink-0" />
            {fromWidgetDialog || fromEditWidgetDialog
              ? "Select a query to use in your dashboard widget"
              : "Select a query to use in your workflow node"}
          </div>
          <Button size="sm" variant="outline" onClick={() => {
            if (fromWidgetDialog) {
              const draftRaw = sessionStorage.getItem("widget_draft");
              let dashboardId = "";
              try { dashboardId = draftRaw ? JSON.parse(draftRaw).dashboardId || "" : ""; } catch { /* ignore */ }
              sessionStorage.removeItem("widget_draft");
              navigate(dashboardId ? `/dashboards?dashboard=${dashboardId}` : "/dashboards");
            } else if (fromEditWidgetDialog) {
              const draftRaw = sessionStorage.getItem("edit_widget_draft");
              let dashboardId = "";
              try { dashboardId = draftRaw ? JSON.parse(draftRaw).dashboardId || "" : ""; } catch { /* ignore */ }
              sessionStorage.removeItem("edit_widget_draft");
              navigate(dashboardId ? `/dashboards?dashboard=${dashboardId}` : "/dashboards");
            } else {
              navigate("/aiagents");
            }
          }}>
            <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Cancel
          </Button>
        </div>
      )}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Saved Queries</h2>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setSchemaOpen(true)}>
            <Table2 className="mr-1 h-4 w-4" /> Schema
          </Button>
          <Button size="sm" onClick={handleNew}>
            <Plus className="mr-1 h-4 w-4" /> New Query
          </Button>
        </div>
      </div>

      {queries.length > 0 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search by name…"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            className="pl-9"
          />
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : queries.length === 0 && !nameFilter ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-accent">
            <SearchCode className="h-7 w-7 text-accent-foreground" />
          </div>
          <p className="text-muted-foreground text-sm">No saved queries yet.</p>
          <Button size="sm" variant="outline" onClick={handleNew}>
            Create your first query
          </Button>
        </div>
      ) : (
        <div className="rounded-md border min-h-0 flex-1 overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-2 text-left font-medium">Name</th>
                <th className="px-4 py-2 text-left font-medium">Database</th>
                <th className="px-4 py-2 text-left font-medium">Mode</th>
                <th className="px-4 py-2 text-left font-medium">Updated</th>
                <th className="px-4 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {queries.filter((q) => q.name.toLowerCase().includes(nameFilter.toLowerCase())).map((q) => (
                <tr key={q.id} className="border-b last:border-0">
                  <td
                    className="px-4 py-2 font-medium cursor-pointer hover:text-primary hover:underline"
                    onClick={() => handleEdit(q)}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {toTitleCase(q.name)}
                      {q.is_report && (
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Report</Badge>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {q.pipeline_source_connector?.name ?? "—"}
                  </td>
                  <td className="px-4 py-2">
                    <Badge variant={modeBadgeVariant(q.generation_mode) as any}>
                      {q.generation_mode}
                    </Badge>
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {format(new Date(q.updated_at), "MMM d, yyyy")}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {selectMode && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mr-1 h-7 text-xs text-indigo-700 border-indigo-300 hover:bg-indigo-50"
                        onClick={() => handleSelectQuery(q)}
                      >
                        Use this query
                      </Button>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => toggleBookmark(q.id)}
                      title={q.bookmarked ? "Remove bookmark" : "Bookmark"}
                    >
                      <Bookmark className={cn("h-4 w-4", q.bookmarked ? "fill-primary text-primary" : "text-muted-foreground")} />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setSubscribeQuery(q)}
                      title="Subscribe"
                      className={subscriptionMap.has(q.id) ? "text-primary" : ""}
                    >
                      {subscriptionMap.has(q.id) ? (
                        <BellRing className="h-4 w-4" />
                      ) : (
                        <Bell className="h-4 w-4" />
                      )}
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="icon" variant="ghost">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => toggleReport(q.id)}>
                          <FileText className="mr-2 h-4 w-4" />
                          {q.is_report ? "Unmark as Report" : "Mark as Report"}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => cloneMutation.mutate(q)}>
                          <Copy className="mr-2 h-4 w-4" />
                          Clone
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => deleteMutation.mutate(q.id)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {subscribeQuery && (
        <SubscribeDialog
          open={!!subscribeQuery}
          onOpenChange={(open) => !open && setSubscribeQuery(null)}
          queryId={subscribeQuery.id}
          queryName={subscribeQuery.name}
        />
      )}

      <Dialog open={schemaOpen} onOpenChange={setSchemaOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Table2 className="h-5 w-5" />
              Database Schema
              {schemaData.length > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs">
                  {schemaData.length} {schemaData.length === 1 ? "table" : "tables"}
                </Badge>
              )}
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 ml-auto"
                disabled={schemaLoading}
                onClick={() => refetchSchema()}
                title="Refresh schema"
              >
                <RefreshCw className={cn("h-4 w-4", schemaLoading && "animate-spin")} />
              </Button>
            </DialogTitle>
          </DialogHeader>

          <input
            type="text"
            value={schemaSearch}
            onChange={(e) => setSchemaSearch(e.target.value)}
            placeholder="Search tables or columns…"
            className="h-8 w-full rounded-md border border-input bg-background px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          />

          <div className="flex-1 overflow-y-auto space-y-2 mt-2">
            {schemaLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredSchema.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground pt-10">
                {schemaData.length === 0 ? "No schema available." : "No tables match your search."}
              </p>
            ) : (
              filteredSchema.map((table) => {
                const isOpen = expandedTables.has(table.table_name);
                return (
                  <Collapsible
                    key={table.table_name}
                    open={isOpen}
                    onOpenChange={() => toggleTable(table.table_name)}
                  >
                    <CollapsibleTrigger asChild>
                      <button className="flex w-full items-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 text-left transition-colors hover:bg-accent">
                        {isOpen ? (
                          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                        )}
                        <Table2 className="h-4 w-4 shrink-0 text-primary" />
                        <span className="flex-1 text-sm font-medium">{table.table_name}</span>
                        <Badge variant="outline" className="text-xs">
                          {table.columns.length} col{table.columns.length !== 1 ? "s" : ""}
                        </Badge>
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="mx-1 mb-1 overflow-hidden rounded-b-lg border border-t-0 border-border">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border bg-muted/40">
                              <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Column</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Type</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Nullable</th>
                            </tr>
                          </thead>
                          <tbody>
                            {table.columns.map((col, idx) => (
                              <tr
                                key={col.column_name}
                                className={cn(
                                  "border-b border-border last:border-0",
                                  idx % 2 === 0 ? "bg-background" : "bg-muted/20"
                                )}
                              >
                                <td className="flex items-center gap-2 px-4 py-2">
                                  <Columns className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                  <span className="font-mono text-xs">{col.column_name}</span>
                                </td>
                                <td className="px-4 py-2">
                                  <Badge variant="secondary" className="font-mono text-xs">{col.data_type}</Badge>
                                </td>
                                <td className="px-4 py-2 text-xs text-muted-foreground">
                                  {col.is_nullable === "YES" || col.is_nullable === "true" || col.is_nullable === undefined ? "Yes" : "No"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SavedQueries;
